import { fetcher } from '#lib/backendFetcher';
import { filtersToQueryParams } from '#lib/tableFilters';

/**
 * Fetches one page of the org's contact blacklist entries (domains + emails never to be contacted).
 * @param {Object} params
 * @param {number} params.offset - Rows to skip
 * @param {number} params.limit - Page size, capped at 100 by the API
 * @param {{entryType?: Array<'email'|'domain'>, search?: string}} [params.filters]
 * @returns {Promise<{entries: Object[], total: number}>} Page of entries plus the filtered total
 */
export const fetchContactBlacklist = async ({ offset, limit, filters = {} }) => {
	const params = new URLSearchParams({ offset, limit, ...filtersToQueryParams(filters) });
	const data = await fetcher(`/api/v1/contact-lists/blacklist?${params}`);
	return { entries: data?.entries ?? [], total: data?.total ?? 0 };
};

/**
 * Adds contact blacklist entries. Entries already on the blacklist stay unchanged; invalid values and
 * freemail domains are skipped, and the request fails only when none of them can be blocked.
 * @param {Object} body
 * @param {Array<{entryType: 'email'|'domain', value: string}>} body.entries - 1–500 values; a domain value may be a URL or an email
 * @param {string} [body.reason]
 * @returns {Promise<{added: number, alreadyBlocked: number, skipped: number}>}
 */
export const createBlacklistEntries = async (body) => {
	return fetcher('/api/v1/contact-lists/blacklist', { method: 'POST', body });
};

/**
 * Removes a contact blacklist entry.
 * @param {string} entryId - The ID of the entry to remove
 * @returns {Promise<{error: null}>} Null on success
 */
export const deleteBlacklistEntry = async (entryId) => {
	await fetcher(`/api/v1/contact-lists/blacklist/${entryId}`, { method: 'DELETE' });
	return { error: null };
};

/**
 * Removes a scope of contact blacklist entries.
 * @param {{ids: string[]}|{selectAll: true, filters?: {entryType?: Array<'email'|'domain'>, search?: string}}} scope
 * @returns {Promise<{removed: number}>}
 */
export const bulkDeleteBlacklistEntries = (scope) =>
	fetcher('/api/v1/contact-lists/blacklist/bulk-delete', { method: 'POST', body: scope });
